<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
      >
        <div class="fixed inset-0" @click="onCancel"></div>

        <!-- Modal container -->
        <div
          ref="dialogRef"
          tabindex="-1"
          :class="[
            'relative bg-box-container-bg dark:bg-box-container-bg-dark rounded-xl shadow-modal w-full border border-container-border dark:border-container-border-dark z-50 flex flex-col focus:outline-none',
            sizeClass,
          ]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="$slots.header ? undefined : titleId"
          :aria-label="$slots.header ? title : undefined"
        >
          <!-- Header -->
          <div v-if="$slots.header" class="flex-shrink-0">
            <slot name="header"></slot>
          </div>
          <div
            v-else
            class="flex justify-between items-center gap-4 p-5 sm:p-6 pb-3 flex-shrink-0"
          >
            <h3
              :id="titleId"
              class="min-w-0 truncate text-lg font-semibold text-heading dark:text-heading-dark"
            >
              {{ title }}
            </h3>
            <button
              type="button"
              @click="onCancel"
              class="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-md text-content dark:text-content-dark hover:bg-secondary-bg dark:hover:bg-secondary-bg-dark hover:text-heading dark:hover:text-heading-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              :aria-label="t('components.modal.close')"
            >
              <mdi-close class="text-xl" aria-hidden="true" />
            </button>
          </div>

          <!-- Body -->
          <div
            class="px-5 sm:px-6 py-3 overflow-y-auto flex-grow max-h-[calc(90dvh-130px)]"
          >
            <!-- Message -->
            <p v-if="message" class="text-sm leading-relaxed text-content dark:text-content-dark mb-4">
              {{ message }}
            </p>

            <!-- Default slot content -->
            <slot></slot>

            <!-- Confirmation input if needed -->
            <div v-if="confirmText" class="mt-5">
              <p class="mb-2 text-content dark:text-content-dark">
                {{ t("components.modal.typeToConfirm", { text: confirmText }) }}
              </p>
              <Input
                v-model="userInput"
                :placeholder="
                  t('components.modal.typeTextToConfirm', { text: confirmText })
                "
                @keyup.enter="isConfirmEnabled && onConfirm()"
              />
            </div>
          </div>

          <!-- Footer -->
          <div v-if="$slots.footer" class="flex-shrink-0">
            <slot name="footer"></slot>
          </div>
          <div v-else class="flex flex-wrap justify-end gap-2.5 px-5 sm:px-6 py-4 flex-shrink-0">
            <Button variant="secondary" @click="onCancel">
              {{ cancelText || t("components.modal.cancel") }}
            </Button>
            <Button
              v-if="confirmButtonText"
              :variant="confirmVariant"
              :disabled="!isConfirmEnabled"
              @click="onConfirm"
            >
              {{ confirmButtonText }}
            </Button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRef } from "vue";
import { useI18n } from "vue-i18n";
import Button from "./Button.vue";
import Input from "./Input.vue";
import { useFocusTrap } from "../../composables/use-focus-trap";

const { t } = useI18n({ useScope: "global" });

interface Props {
  modelValue: boolean;
  title: string;
  message?: string;
  confirmText?: string;
  confirmVariant?: "primary" | "secondary" | "danger";
  cancelText?: string;
  confirmButtonText?: string;
  size?: "small" | "medium" | "wide" | "large" | "xlarge";
}

const props = withDefaults(defineProps<Props>(), {
  message: "",
  confirmText: "",
  confirmVariant: "danger",
  cancelText: "",
  size: "medium",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm"): void;
  (e: "cancel"): void;
}>();

const userInput = ref("");

const dialogRef = ref<HTMLElement | null>(null);
const titleId = `modal-title-${Math.random().toString(36).slice(2, 8)}`;

// Trap Tab inside the dialog while open, close on Escape and return focus
// to the element that opened it.
useFocusTrap(dialogRef, toRef(props, "modelValue"), {
  onEscape: () => onCancel(),
});

// Determine modal size class based on the size prop
const sizeClass = computed(() => {
  switch (props.size) {
    case "small":
      return "max-w-sm";
    case "wide":
      return "max-w-xl";
    case "large":
      return "max-w-5xl";
    case "xlarge":
      return "max-w-7xl";
    default:
      return "max-w-md";
  }
});

// Determine if the confirm button should be enabled
const isConfirmEnabled = computed(() => {
  if (props.confirmText) {
    return userInput.value === props.confirmText;
  }
  return true;
});

// Reset input when modal opens/closes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      userInput.value = "";
    }
  },
);

// Handle confirm action
const onConfirm = () => {
  if (isConfirmEnabled.value) {
    emit("confirm");
    emit("update:modelValue", false);
  }
};

// Handle cancel action
const onCancel = () => {
  emit("cancel");
  emit("update:modelValue", false);
};
</script>
